import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import { themeAtom } from "../../shared/atoms";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { HoldCleaningAssessmentPanel } from "../components/HoldCleaningAssessmentPanel";
import { HoldCleaningImagePreviewModal } from "../components/HoldCleaningImagePreviewModal";
import { HoldCleaningManualEntryModal } from "../components/HoldCleaningManualEntryModal";
import { HoldCleaningNoteModal } from "../components/HoldCleaningNoteModal";
import { HoldCleaningPhotoUploadModal } from "../components/HoldCleaningPhotoUploadModal";
import { HoldCleaningReports } from "../components/HoldCleaningReports";
import {
  describeHoldCleaningStep,
  HOLD_CLEANING_SOURCE_TOOLTIP_ID,
  HoldCleaningSourceHint,
  HoldCleaningHoldCountHint,
  HoldCleaningSourceTooltip,
} from "../components/HoldCleaningSourceHint";
import {
  formatHoldCleaningDate,
  HoldCleaningAuthenticatedImage,
  HoldCleaningMockBanner,
  HoldCleaningPageState,
  HoldCleaningStatusBadge,
} from "../components/HoldCleaningUi";
import {
  IMAGE_CATEGORY_LABELS,
  filterImageCategory,
  type ImageCategoryFilter,
} from "../components/hold-cleaning-image-categories";
import { CLEANING_STEPS } from "../components/hold-cleaning-steps";
import type {
  HoldCleaningAssessmentResult,
  HoldCleaningCrewSummary,
  HoldCleaningFormField,
  HoldCleaningImage,
  HoldCleaningVoyageDetail,
  HoldCleaningVoyageSummary,
  HoldCleaningManualEntryInput,
  HoldCleaningProjectionSource,
} from "../models/hold-cleaning.models";
import {
  addHoldCleaningNote,
  createHoldCleaningManualEntry,
  downloadHoldCleaningRecord,
  getHoldCleaningAssessment,
  getHoldCleaningCrew,
  getHoldCleaningErrorMessage,
  getHoldCleaningImages,
  getHoldCleaningVoyage,
  getHoldCleaningVesselHistory,
  uploadHoldCleaningPhotos,
} from "../services/hold-cleaning.service";
import {
  ArrowLeftIcon,
  ArrowsPointingOutIcon,
  CameraIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { useAtomValue } from "jotai";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type DetailTab = "current" | "historical" | "suggestions";

const CLEANING_TASKS = CLEANING_STEPS;

const NOT_IMPLEMENTED = "Not available";

const projectionSourceLabel: Record<HoldCleaningProjectionSource, string> = {
  EMAIL_INTELLIGENCE: "Email intelligence",
  MANUAL: "Manual correction",
  FORM: "Hold Cleaning form",
  SNOWFLAKE: "Snowflake",
};

const blankAssessment = (voyageId: string): HoldCleaningAssessmentResult => ({
  etag: "*",
  assessment: {
    voyageId,
    assessmentId: "",
    revision: 0,
    payload: {
      readiness: "UNASSESSED",
      summary: "",
      actions: [],
      evidence: [],
      imageIds: [],
    },
    createdBy: "",
    createdAt: "",
    source: "HUMAN",
  },
});

const sectionClass = "rounded-lg border border-[#DFE4E2] dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm";

const displayValue = (value: HoldCleaningFormField["value"]) => {
  if (value === null || value === undefined || value === "") return "Not available";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const Fact = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#899996] dark:text-gray-300">{label}</dt>
    <dd className="mt-1 break-words text-sm font-medium text-[#292F2D] dark:text-gray-100">
      {value ?? "Not available"}
    </dd>
  </div>
);

const EmptySection = ({ children }: { children: ReactNode }) => (
  <p className="rounded-lg border border-dashed border-[#C7D0CE] bg-[#F8F9F9] px-4 py-6 text-center text-sm text-[#4A5655] dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300">
    {children}
  </p>
);

const NotImplementedValue = ({ label = NOT_IMPLEMENTED }: { label?: string }) => (
  <span className="inline-flex rounded-full bg-[#F3F4F4] px-2.5 py-1 text-xs font-semibold text-[#899996] dark:bg-gray-900 dark:text-gray-300">
    {label}
  </span>
);

const metricValue = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? <NotImplementedValue /> : value;

const HoldCleaningVoyagePage = () => {
  const theme = useAtomValue(themeAtom);
  const navigate = useNavigate();
  const { voyageId = "", imo = "" } = useParams();
  const decodedVoyageId = decodeURIComponent(voyageId);
  const [tab, setTab] = useState<DetailTab>("historical");
  const [detail, setDetail] = useState<HoldCleaningVoyageDetail | null>(null);
  const [relatedVoyages, setRelatedVoyages] = useState<HoldCleaningVoyageSummary[]>([]);
  const [crew, setCrew] = useState<HoldCleaningCrewSummary | null>(null);
  const [images, setImages] = useState<HoldCleaningImage[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null);
  const [imageCategory, setImageCategory] = useState<ImageCategoryFilter>("ALL");
  const [assessment, setAssessment] = useState<HoldCleaningAssessmentResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partialErrors, setPartialErrors] = useState<string[]>([]);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const loadGeneration = useRef(0);

  const load = useCallback(async () => {
    if (!decodedVoyageId && !imo) return;
    const generation = ++loadGeneration.current;
    log("HoldCleaningVoyagePage - load", { voyageId: decodedVoyageId || undefined, byImo: !decodedVoyageId });
    setLoading(true);
    setError(null);
    setPartialErrors([]);
    setDetail(null);
    setImages([]);
    setPreviewImageIndex(null);
    setImageCategory("ALL");
    setAssessment(null);

    try {
      const voyageDetail = await getHoldCleaningVoyage(decodedVoyageId || imo, !decodedVoyageId);
      if (generation !== loadGeneration.current) return;
      setDetail(voyageDetail);
      setCrew(voyageDetail.crew ?? null);

      const [crewResult, imagesResult, assessmentResult, relatedResult] = await Promise.allSettled([
        getHoldCleaningCrew(voyageDetail.voyage.voyageId),
        getHoldCleaningImages(voyageDetail.voyage.voyageId),
        getHoldCleaningAssessment(voyageDetail.voyage.voyageId),
        getHoldCleaningVesselHistory(voyageDetail.voyage.imo),
      ]);
      if (generation !== loadGeneration.current) return;

      const errors: string[] = [];
      if (crewResult.status === "fulfilled") {
        setCrew(crewResult.value);
      } else {
        setCrew(null);
        errors.push(`Crew summary: ${getHoldCleaningErrorMessage(crewResult.reason)}`);
      }

      if (imagesResult.status === "fulfilled") {
        setImages(imagesResult.value);
      } else {
        errors.push(`Images: ${getHoldCleaningErrorMessage(imagesResult.reason)}`);
      }

      if (assessmentResult.status === "fulfilled") {
        setAssessment(assessmentResult.value);
      } else if (axios.isAxiosError(assessmentResult.reason) && assessmentResult.reason.response?.status === 404) {
        setAssessment(blankAssessment(voyageDetail.voyage.voyageId));
      } else {
        errors.push(`Assessment: ${getHoldCleaningErrorMessage(assessmentResult.reason)}`);
      }

      if (relatedResult.status === "fulfilled") {
        setRelatedVoyages(relatedResult.value.items);
      } else {
        setRelatedVoyages([voyageDetail.voyage]);
        errors.push(`Voyage history: ${getHoldCleaningErrorMessage(relatedResult.reason)}`);
      }
      const failedSources = [
        { source: "crew", result: crewResult },
        { source: "images", result: imagesResult },
        { source: "assessment", result: assessmentResult },
        { source: "history", result: relatedResult },
      ].flatMap(({ source, result }) => {
        if (result.status !== "rejected" || axios.isCancel(result.reason)) return [];
        const status = axios.isAxiosError(result.reason) ? result.reason.response?.status : undefined;
        if (source === "assessment" && status === 404) return [];
        return [`${source}:${status ?? (axios.isAxiosError(result.reason) ? "no-response" : "unexpected")}`];
      });
      log("STATE - load - hold cleaning detail", {
        images: imagesResult.status === "fulfilled" ? imagesResult.value.length : 0,
        failures: failedSources.join(",") || "none",
      });
      setPartialErrors(errors);
    } catch (requestError) {
      if (generation === loadGeneration.current) {
        if (!axios.isCancel(requestError)) logE(requestError, { operation: "HoldCleaningVoyagePage - load - failed" });
        setError(getHoldCleaningErrorMessage(requestError));
      }
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [decodedVoyageId, imo]);

  useEffect(() => {
    void load();
    return () => {
      loadGeneration.current += 1;
    };
  }, [load]);

  const formFields = useMemo(
    () => detail?.forms.flatMap((form) => form.sections.flatMap((section) => section.fields)) ?? [],
    [detail],
  );
  const inventoryFields = useMemo(
    () =>
      formFields.filter((field) =>
        /chemical|detergent|equipment|machine|inventory|stock|rob|quantity|consumable/i.test(field.label),
      ),
    [formFields],
  );
  const inventoryRows = useMemo(
    () =>
      detail?.currentCleaning?.inventory?.length
        ? detail.currentCleaning.inventory.map((item) => ({
            label: item.label,
            value: item.value,
            unit: item.unit,
            source: item.source,
          }))
        : inventoryFields.map((item) => ({ ...item, source: "FORM" as const })),
    [detail, inventoryFields],
  );
  const imageGroups = useMemo(() => {
    const groups = new Map<string, HoldCleaningImage[]>();
    filterImageCategory(images, imageCategory).forEach((image) => {
      const key =
        image.holdNumber === null || image.holdNumber === undefined ? "Unassigned" : `Hold ${image.holdNumber}`;
      groups.set(key, [...(groups.get(key) ?? []), image]);
    });
    return Array.from(groups.entries()).sort(([left], [right]) =>
      left.localeCompare(right, undefined, { numeric: true }),
    );
  }, [images, imageCategory]);
  const previewImages = useMemo(() => imageGroups.flatMap(([, holdImages]) => holdImages), [imageGroups]);
  const previewImage = previewImageIndex === null ? null : previewImages[previewImageIndex] ?? null;
  const showPreviousImage = useCallback(() => {
    setPreviewImageIndex((index) =>
      index === null ? null : (index - 1 + previewImages.length) % previewImages.length,
    );
  }, [previewImages.length]);
  const showNextImage = useCallback(() => {
    setPreviewImageIndex((index) => (index === null ? null : (index + 1) % previewImages.length));
  }, [previewImages.length]);

  const warningMessages = useMemo(
    () => [
      ...(detail?.dataset.warnings.map((warning) => warning.message) ?? []),
      ...(detail?.warnings.map((warning) => warning.message) ?? []),
      ...(crew?.warnings.map((warning) => warning.message) ?? []),
      ...(detail?.history?.warnings
        ?.filter((warning) => warning.state === "OPEN")
        .map((warning) => warning.description) ?? []),
      ...partialErrors,
    ],
    [crew, detail, partialErrors],
  );
  const actionCount = assessment?.assessment.payload.actions.length ?? 0;
  const suggestionCount = warningMessages.length + actionCount;

  const tabs: Array<{ id: DetailTab; label: string; count?: number }> = [
    { id: "current", label: "Current cleaning" },
    { id: "historical", label: "Historical data" },
    { id: "suggestions", label: "Suggestions and warnings", count: suggestionCount },
  ];

  const saveManualEntry = async (input: HoldCleaningManualEntryInput) => {
    log("HoldCleaningVoyagePage - saveManualEntry");
    await createHoldCleaningManualEntry(detail!.voyage.voyageId, input);
    await load();
  };
  const saveNote = async (text: string) => {
    log("HoldCleaningVoyagePage - saveNote");
    await addHoldCleaningNote(detail!.voyage.voyageId, text);
    await load();
  };
  const savePhotos = async (files: File[], holdNumber: number | null, caption: string) => {
    log("HoldCleaningVoyagePage - savePhotos", { count: files.length });
    await uploadHoldCleaningPhotos(detail!.voyage.voyageId, files, holdNumber, caption);
    await load();
  };
  const exportRecord = async () => {
    log("HoldCleaningVoyagePage - exportRecord");
    setExporting(true);
    setActionError(null);
    try {
      const { blob, filename } = await downloadHoldCleaningRecord(detail!.voyage.voyageId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      if (!axios.isCancel(requestError))
        logE(requestError, { operation: "HoldCleaningVoyagePage - exportRecord - failed" });
      setActionError(getHoldCleaningErrorMessage(requestError));
    } finally {
      setExporting(false);
    }
  };
  const openEvaluation = () => {
    log("HoldCleaningVoyagePage - openEvaluation");
    setTab("historical");
    window.setTimeout(
      () => document.getElementById("hold-cleaning-assessment")?.scrollIntoView({ behavior: "smooth" }),
      0,
    );
  };

  logV("HoldCleaningVoyagePage - BUILD", { loading, tab });

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className={twMerge("flex min-w-0 flex-1 flex-col overflow-y-auto px-6", theme.home.tailwind.pageBackground)}>
        <Header />
        <HoldCleaningMockBanner />
        <main className="mx-auto flex w-full max-w-[1680px] flex-1 flex-col gap-5 px-2 pb-10 text-[#292F2D] dark:text-gray-100">
          <HoldCleaningSourceTooltip />
          <button
            type="button"
            onClick={() => navigate("/hold-cleaning")}
            className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-[#2F7F7B] hover:underline dark:text-teal-200"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Hold Cleaning list
          </button>

          {loading && !detail ? (
            <HoldCleaningPageState title="Loading voyage history" message="Combining the active source evidence..." />
          ) : error && !detail ? (
            <HoldCleaningPageState
              title="Voyage history could not be loaded"
              message={error}
              tone="error"
              action={
                <button
                  type="button"
                  onClick={load}
                  className="rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold dark:bg-gray-800"
                >
                  Try again
                </button>
              }
            />
          ) : detail ? (
            <>
              <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-[30px] font-semibold leading-tight">{detail.voyage.vesselName}</h1>
                  <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                    Hold cleaning record and supporting operational evidence
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <HoldCleaningStatusBadge value={detail.voyage.status} className="px-3 py-1.5" />
                  <button
                    type="button"
                    onClick={() => setManualEntryOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#1F8475] px-3 py-2 text-xs font-semibold text-white"
                  >
                    <PlusIcon className="h-4 w-4" />
                    New cleaning entry
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhotoUploadOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#C7D0CE] bg-white px-3 py-2 text-xs font-semibold dark:border-gray-600 dark:bg-gray-800"
                  >
                    <CameraIcon className="h-4 w-4" />
                    Upload photos
                  </button>
                  <button
                    type="button"
                    onClick={() => setNoteOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#C7D0CE] bg-white px-3 py-2 text-xs font-semibold dark:border-gray-600 dark:bg-gray-800"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add note
                  </button>
                  <button
                    type="button"
                    onClick={openEvaluation}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#C7D0CE] bg-white px-3 py-2 text-xs font-semibold dark:border-gray-600 dark:bg-gray-800"
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                    New evaluation
                  </button>
                  <button
                    type="button"
                    disabled={exporting}
                    onClick={exportRecord}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#C7D0CE] bg-white px-3 py-2 text-xs font-semibold disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    {exporting ? "Exporting..." : "Export record"}
                  </button>
                </div>
              </header>

              {actionError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {actionError}
                </div>
              )}

              <section className={sectionClass} aria-label="Vessel facts">
                <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
                  <Fact
                    label="Built"
                    value={
                      detail.vesselFacts?.builtAt
                        ? new Date(detail.vesselFacts.builtAt).getUTCFullYear()
                        : "Not available"
                    }
                  />
                  <Fact
                    label="IMO number"
                    value={
                      <>
                        {detail.voyage.imo}
                        <span className="mt-1 block text-xs font-normal text-[#899996] dark:text-gray-300">
                          {detail.vesselFacts?.shipType ?? NOT_IMPLEMENTED}
                        </span>
                      </>
                    }
                  />
                  <Fact
                    label="Registered owner"
                    value={
                      <>
                        {metricValue(detail.vesselFacts?.registeredOwner)}
                        <span className="mt-1 block text-xs font-normal text-[#899996] dark:text-gray-300">
                          Manager: {detail.vesselFacts?.shipManager ?? NOT_IMPLEMENTED}
                        </span>
                      </>
                    }
                  />
                  <Fact
                    label="Size"
                    value={
                      <>
                        {detail.vesselFacts?.deadweightTonnes
                          ? `${detail.vesselFacts.deadweightTonnes.toLocaleString()} DWT`
                          : NOT_IMPLEMENTED}
                        <span className="mt-1 block text-xs font-normal text-[#899996] dark:text-gray-300">
                          {detail.voyage.numberOfHolds ?? NOT_IMPLEMENTED} holds /{" "}
                          {detail.vesselFacts?.numberOfHatches ?? NOT_IMPLEMENTED} hatches
                          <HoldCleaningHoldCountHint evidence={detail.vesselFacts?.holdCountEvidence} />
                        </span>
                      </>
                    }
                  />
                  <Fact
                    label="Grain capacity"
                    value={
                      <>
                        {detail.vesselFacts?.grainCapacityCubicFeet
                          ? `${detail.vesselFacts.grainCapacityCubicFeet.toLocaleString()} cbft`
                          : NOT_IMPLEMENTED}
                        <span className="mt-1 block text-xs font-normal text-[#899996] dark:text-gray-300">
                          {detail.vesselFacts?.gearDescription ?? NOT_IMPLEMENTED}
                        </span>
                      </>
                    }
                  />
                  <Fact label="Last star rating" value="—" />
                </dl>
                <p className="mt-5 border-t border-[#E8ECEB] pt-3 text-xs text-[#899996] dark:border-gray-700 dark:text-gray-300">
                  1 star mineral clean - 2 stars grain clean - 3 stars hospital clean
                </p>
              </section>

              <nav
                className="border-b border-[#C7D0CE] dark:border-gray-600"
                aria-label="Hold cleaning record sections"
              >
                <div className="flex gap-7 overflow-x-auto">
                  {tabs.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setTab(item.id)}
                      className={twMerge(
                        "relative whitespace-nowrap px-1 pb-3 text-sm font-semibold",
                        tab === item.id
                          ? "text-[#1F8475] dark:text-teal-300"
                          : "text-[#4A5655] hover:text-[#1F8475] dark:text-gray-300 dark:hover:text-teal-300",
                      )}
                    >
                      {item.label}
                      {item.count !== undefined && item.count > 0 && (
                        <span className="ml-2 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-xs text-[#92400E] dark:bg-amber-950 dark:text-amber-200">
                          {item.count}
                        </span>
                      )}
                      {tab === item.id && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#1F8475]" />}
                    </button>
                  ))}
                </div>
              </nav>

              {tab === "current" && (
                <div className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {[
                      {
                        label: "Report",
                        value: detail.currentCleaning?.reportName ?? NOT_IMPLEMENTED,
                        note: detail.currentCleaning?.reportUpdatedAt
                          ? formatHoldCleaningDate(detail.currentCleaning.reportUpdatedAt, false)
                          : NOT_IMPLEMENTED,
                        icon: DocumentTextIcon,
                      },
                      {
                        label: "Cleaning steps",
                        value:
                          detail.currentCleaning?.completedSteps === null ||
                          detail.currentCleaning?.completedSteps === undefined
                            ? NOT_IMPLEMENTED
                            : `${detail.currentCleaning.completedSteps} of ${detail.currentCleaning.totalSteps} complete`,
                        note: `Across ${detail.voyage.numberOfHolds ?? NOT_IMPLEMENTED} holds`,
                        icon: CheckCircleIcon,
                      },
                      {
                        label: "Outstanding",
                        value: detail.currentCleaning?.outstandingItem ?? NOT_IMPLEMENTED,
                        note: "Next required cleaning activity",
                        icon: ExclamationTriangleIcon,
                      },
                      {
                        label: "Work log",
                        value:
                          detail.currentCleaning?.workLogDays === null ||
                          detail.currentCleaning?.workLogDays === undefined
                            ? NOT_IMPLEMENTED
                            : `${detail.currentCleaning.workLogDays} days`,
                        note: "Daily reports from the vessel",
                        icon: ClockIcon,
                      },
                    ].map((metric) => (
                      <section key={metric.label} className={sectionClass}>
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-[#899996] dark:text-gray-300">
                              {metric.label}
                            </p>
                            <p className="mt-2 text-xl font-semibold text-[#292F2D] dark:text-gray-100">
                              {metric.value}
                            </p>
                            <p className="mt-1 text-xs text-[#899996] dark:text-gray-300">{metric.note}</p>
                          </div>
                          <metric.icon className="h-7 w-7 text-[#1F8475] dark:text-teal-300" aria-hidden="true" />
                        </div>
                      </section>
                    ))}
                  </div>

                  <section className={sectionClass}>
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold">Hold cleaning progress</h2>
                        <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                          Standard cleaning steps by cargo hold.
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[1500px] text-left text-xs">
                        <thead className="bg-[#DFF2F0] text-[10px] uppercase tracking-wide text-[#2F7F7B] dark:bg-teal-950 dark:text-teal-200">
                          <tr>
                            <th className="sticky left-0 z-10 w-[110px] bg-[#DFF2F0] px-3 py-3 dark:bg-teal-950">
                              Cargo hold
                            </th>
                            {CLEANING_TASKS.map((task, index) => (
                              <th key={task} className="min-w-[125px] px-3 py-3 align-top">
                                <span className="block text-[#1F8475] dark:text-teal-300">
                                  {String(index + 1).padStart(2, "0")}
                                </span>
                                <span className="mt-1 block normal-case tracking-normal text-[#4A5655] dark:text-gray-300">
                                  {task}
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E8ECEB] dark:divide-gray-700">
                          {Array.from({ length: detail.voyage.numberOfHolds ?? 0 }, (_, index) => index + 1).map(
                            (holdNumber) => (
                              <tr key={holdNumber}>
                                <th className="sticky left-0 bg-white px-3 py-3 text-sm font-semibold dark:bg-gray-800">
                                  Hold {holdNumber}
                                </th>
                                {CLEANING_TASKS.map((task, taskIndex) => {
                                  const taskState = detail.currentCleaning?.taskStatuses.find(
                                    (item) => item.holdNumber === holdNumber && item.stepNumber === taskIndex + 1,
                                  );
                                  const taskStatus = taskState?.status;
                                  const sourceLabel = taskState?.source
                                    ? projectionSourceLabel[taskState.source]
                                    : null;
                                  return (
                                    <td key={task} className="px-3 py-3">
                                      <span
                                        tabIndex={0}
                                        aria-label={`${taskStatus?.replaceAll("_", " ") ?? "No update"}. ${describeHoldCleaningStep(taskState)}`}
                                        data-tooltip-id={HOLD_CLEANING_SOURCE_TOOLTIP_ID}
                                        data-tooltip-content={describeHoldCleaningStep(taskState)}
                                        className={twMerge(
                                          "inline-flex cursor-help rounded-full px-2 py-1 text-[10px] font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475]",
                                          taskStatus === "COMPLETED"
                                            ? "bg-[#DFF3E7] text-[#2B8A57] dark:bg-emerald-950 dark:text-emerald-200"
                                            : taskStatus === "IN_PROGRESS"
                                              ? "bg-[#FEF3E2] text-[#B25E09] dark:bg-amber-950 dark:text-amber-200"
                                              : taskStatus === "PLANNING"
                                                ? "bg-[#F3F4F4] text-[#4A5655] dark:bg-gray-900 dark:text-gray-300"
                                                : "bg-[#F3F4F4] text-[#899996] dark:bg-gray-900 dark:text-gray-300",
                                        )}
                                      >
                                        {taskStatus?.replaceAll("_", " ") ?? "No update"}
                                      </span>
                                      {sourceLabel && <span className="sr-only">Source: {sourceLabel}</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                            ),
                          )}
                          {!detail.voyage.numberOfHolds && (
                            <tr>
                              <td
                                colSpan={14}
                                className="px-4 py-8 text-center text-sm text-[#899996] dark:text-gray-300"
                              >
                                The vessel hold count is unavailable.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
                    <section className={sectionClass}>
                      <h2 className="text-lg font-semibold">Work log</h2>
                      <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                        Daily cleaning reports from the master.
                      </p>
                      <HoldCleaningReports key={detail.voyage.voyageId} voyageId={detail.voyage.voyageId} />
                      <h3 className="mt-5 text-sm font-semibold">Extracted and manually recorded work</h3>
                      {detail.currentCleaning?.workLog.length ? (
                        <ol className="mt-5 space-y-4 border-l border-[#BBDDD8] pl-5 dark:border-teal-800">
                          {detail.currentCleaning.workLog.map((entry) => (
                            <li key={`${entry.occurredAt}-${entry.description}`}>
                              <p className="text-xs font-semibold text-[#2F7F7B] dark:text-teal-200">
                                {formatHoldCleaningDate(entry.occurredAt, false)}
                              </p>
                              <p className="mt-1 text-sm text-[#292F2D] dark:text-gray-100">
                                {entry.description}
                                <HoldCleaningSourceHint source={entry.source} />
                              </p>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <div className="mt-4">
                          <EmptySection>No structured work entries reported.</EmptySection>
                        </div>
                      )}
                    </section>

                    <section className={sectionClass}>
                      <h2 className="text-lg font-semibold">Progress by hold</h2>
                      <div className="mt-5 space-y-4">
                        {Array.from({ length: detail.voyage.numberOfHolds ?? 0 }, (_, index) => index + 1).map(
                          (holdNumber) => {
                            const progress = detail.currentCleaning?.holdProgress.find(
                              (item) => item.holdNumber === holdNumber,
                            );
                            return (
                              <div key={holdNumber}>
                                <div className="flex items-center justify-between gap-3 text-sm">
                                  <span className="font-semibold">Hold {holdNumber}</span>
                                  <span className="text-[#899996] dark:text-gray-300">
                                    {progress ? `${progress.percentage}%` : "No update"}
                                  </span>
                                </div>
                                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#E8ECEB]">
                                  <div
                                    className="h-full rounded-full bg-[#1F8475]"
                                    style={{ width: progress ? `${progress.percentage}%` : "0%" }}
                                  />
                                </div>
                                <p className="mt-1 text-xs text-[#899996] dark:text-gray-300">
                                  {progress?.stage ?? "No progress reported"}
                                  <HoldCleaningSourceHint source={progress?.source} />
                                </p>
                              </div>
                            );
                          },
                        )}
                        {!detail.voyage.numberOfHolds && (
                          <EmptySection>The vessel hold count is unavailable.</EmptySection>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              )}

              {tab === "historical" && (
                <div className="grid items-start gap-5 xl:grid-cols-[250px_minmax(0,1fr)]">
                  <aside className={twMerge(sectionClass, "p-0")}>
                    <div className="border-b border-[#DFE4E2] px-4 py-4 dark:border-gray-700">
                      <h2 className="font-semibold">Recent voyages</h2>
                      <p className="mt-1 text-xs text-[#899996] dark:text-gray-300">Same vessel and IMO</p>
                    </div>
                    <div className="max-h-[760px] overflow-y-auto p-2">
                      {relatedVoyages.map((voyage) => {
                        const selected = voyage.voyageId === detail.voyage.voyageId;
                        return (
                          <button
                            key={voyage.voyageId}
                            type="button"
                            onClick={() => navigate(`/hold-cleaning/voyages/${encodeURIComponent(voyage.voyageId)}`)}
                            className={twMerge(
                              "mb-2 w-full rounded-lg border p-3 text-left transition-colors",
                              selected
                                ? "border-[#9CCFC8] bg-[#E9F4F3] dark:border-teal-700 dark:bg-teal-950"
                                : "border-transparent hover:border-[#DFE4E2] hover:bg-[#F8F9F9] dark:hover:border-gray-700 dark:hover:bg-gray-900",
                            )}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold">
                                Voyage {voyage.voyageNumber ?? voyage.sourceVoyageKey}
                              </span>
                              {selected && <span className="h-2 w-2 rounded-full bg-[#1F8475]" aria-label="Selected" />}
                            </div>
                            <p className="mt-2 text-xs text-[#4A5655] dark:text-gray-300">
                              {formatHoldCleaningDate(voyage.startAt, false)}
                            </p>
                            <p className="mt-1 text-xs text-[#4A5655] dark:text-gray-300">
                              {voyage.cargo ?? voyage.previousCargo ?? "Cargo unavailable"}
                            </p>
                            <p className="mt-2 text-xs font-medium text-[#2F7F7B] dark:text-teal-200">
                              {voyage.status.replaceAll("_", " ")}
                            </p>
                            {(voyage.imageCount ?? 0) > 0 && (
                              <p className="mt-1 text-xs font-semibold text-[#176B62] dark:text-teal-200">
                                {voyage.imageCount} photos
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </aside>

                  <div className="space-y-5">
                    <section className={sectionClass}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-[#2F7F7B] dark:text-teal-200">
                            Selected voyage
                          </p>
                          <h2 className="mt-1 text-xl font-semibold">
                            Voyage {detail.voyage.voyageNumber ?? detail.voyage.sourceVoyageKey}
                          </h2>
                          <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                            {formatHoldCleaningDate(detail.voyage.startAt, false)} -{" "}
                            {formatHoldCleaningDate(detail.voyage.completedAt, false)}
                          </p>
                        </div>
                        <HoldCleaningStatusBadge value={detail.voyage.status} />
                      </div>
                    </section>

                    <section className={sectionClass}>
                      <h2 className="text-lg font-semibold">Cargo transition and schedule</h2>
                      <dl className="mt-4 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                        <Fact
                          label="Current / last cargo"
                          value={detail.voyage.cargo ?? detail.voyage.previousCargo ?? "Not available"}
                        />
                        <Fact label="Discharge port" value={detail.voyage.dischargePort ?? "Not available"} />
                        <Fact
                          label="Discharge completion"
                          value={formatHoldCleaningDate(detail.voyage.completedAt, false)}
                        />
                        <Fact label="Next cargo" value={detail.voyage.nextCargo ?? "Not available"} />
                        <Fact label="Next load port" value={detail.voyage.nextLoadPort ?? "Not available"} />
                        <Fact label="ETA" value={formatHoldCleaningDate(detail.voyage.nextEta, false)} />
                        <Fact label="Laycan start" value={formatHoldCleaningDate(detail.voyage.laycanStartAt, false)} />
                        <Fact label="Laycan end" value={formatHoldCleaningDate(detail.voyage.laycanEndAt, false)} />
                      </dl>
                    </section>

                    <section className={sectionClass}>
                      <div className="mb-4">
                        <h2 className="text-lg font-semibold">Cleaning data</h2>
                        <p className="mt-1 text-xs text-[#899996] dark:text-gray-300">
                          Cleaning details recorded for this voyage
                        </p>
                      </div>
                      <dl className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                        <Fact
                          label="Cleaning window"
                          value={
                            <>
                              {metricValue(detail.history?.cleaningWindow)}
                              <HoldCleaningSourceHint source={detail.history?.fieldSources?.cleaningWindow} />
                            </>
                          }
                        />
                        <Fact
                          label="Man-hours"
                          value={
                            detail.history?.manHours === null || detail.history?.manHours === undefined ? (
                              <NotImplementedValue />
                            ) : (
                              <>
                                {detail.history.manHours} h
                                <HoldCleaningSourceHint source={detail.history.fieldSources?.manHours} />
                              </>
                            )
                          }
                        />
                        <Fact
                          label="Standard required"
                          value={
                            <>
                              {metricValue(detail.history?.standardRequired)}
                              <HoldCleaningSourceHint source={detail.history?.fieldSources?.standardRequired} />
                            </>
                          }
                        />
                        <Fact
                          label="Method"
                          value={
                            <>
                              {metricValue(detail.history?.cleaningMethod)}
                              <HoldCleaningSourceHint source={detail.history?.fieldSources?.cleaningMethod} />
                            </>
                          }
                        />
                      </dl>
                    </section>

                    <section className={sectionClass}>
                      <h2 className="text-lg font-semibold">People involved</h2>
                      <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                        Current crew by rank. {crew && <HoldCleaningSourceHint source="crew-report" />}
                      </p>
                      {!crew ? (
                        <div className="mt-4">
                          <EmptySection>Crew aggregates are not available for this voyage.</EmptySection>
                        </div>
                      ) : (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <span className="rounded-full bg-[#DFF2F0] px-3 py-1.5 text-sm font-semibold text-[#176B62] dark:bg-teal-950 dark:text-teal-200">
                            Total crew: {crew.totalCrew}
                          </span>
                          {crew.rankCounts.map((rank) => (
                            <span
                              key={rank.designationRank}
                              className="rounded-full bg-[#F3F4F6] px-3 py-1.5 text-sm text-[#4A5655] dark:bg-gray-700 dark:text-gray-300"
                            >
                              {rank.designationRank}: {rank.count}
                            </span>
                          ))}
                        </div>
                      )}
                    </section>

                    <section className={sectionClass}>
                      <h2 className="text-lg font-semibold">Cleaning inventory at that moment</h2>
                      <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                        Chemicals and equipment reported for this voyage.
                      </p>
                      {inventoryRows.length === 0 ? (
                        <div className="mt-4">
                          <EmptySection>No cleaning inventory reported.</EmptySection>
                        </div>
                      ) : (
                        <div className="mt-4 overflow-x-auto">
                          <table className="w-full min-w-[560px] text-left text-sm">
                            <thead className="bg-[#DFF2F0] text-[11px] uppercase tracking-wide text-[#2F7F7B] dark:bg-teal-950 dark:text-teal-200">
                              <tr>
                                <th className="px-4 py-3">Item</th>
                                <th className="px-4 py-3">Reported value</th>
                                <th className="px-4 py-3">Unit</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E8ECEB] dark:divide-gray-700">
                              {inventoryRows.map((field, index) => (
                                <tr key={`${field.label}-${index}`}>
                                  <td className="px-4 py-3 font-medium">
                                    {field.label}
                                    <HoldCleaningSourceHint source={field.source} />
                                  </td>
                                  <td className="px-4 py-3">{displayValue(field.value)}</td>
                                  <td className="px-4 py-3 text-[#4A5655] dark:text-gray-300">{field.unit ?? "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>

                    <section className={sectionClass}>
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h2 className="text-lg font-semibold">Hold image evidence</h2>
                          <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                            Authenticated images grouped by assigned hold.
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-[#899996] dark:text-gray-300">{images.length} images</span>
                          <button
                            type="button"
                            onClick={() => setPhotoUploadOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#C7D0CE] px-3 py-2 text-xs font-semibold text-[#176B62] dark:border-gray-600 dark:text-teal-200"
                          >
                            <CameraIcon className="h-4 w-4" aria-hidden="true" />
                            Upload photos
                          </button>
                        </div>
                      </div>
                      <p className="mb-3 text-xs text-[#4A5655] dark:text-gray-300">
                        Images for selected voyage {detail.voyage.voyageNumber ?? "—"} only. Choose another voyage in
                        history to see its photos. Association with a report does not verify when a photo was taken.
                      </p>
                      {images.some((image) => image.categoryMode === "SHADOW") && (
                        <details className="mb-4 text-sm text-[#4A5655] dark:text-gray-300">
                          <summary className="cursor-pointer">Review image categories (preview)</summary>
                          <p className="my-2 text-xs">
                            Automatic suggestions, not verified labels. All images remain available; this does not
                            change the vessel Photos filter.
                          </p>
                          <label className="flex items-center gap-2 text-[#4A5655] dark:text-gray-300">
                            Show
                            <select
                              aria-label="Image category"
                              value={imageCategory}
                              onChange={(event) => {
                                setImageCategory(event.target.value as ImageCategoryFilter);
                                setPreviewImageIndex(null);
                              }}
                              className="rounded border border-[#C7D0CE] bg-white px-2 py-1 text-[#292F2D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:[color-scheme:dark]"
                            >
                              {(Object.entries(IMAGE_CATEGORY_LABELS) as Array<[ImageCategoryFilter, string]>).map(
                                ([value, label]) => (
                                  <option key={value} value={value}>
                                    {label} ({filterImageCategory(images, value).length})
                                  </option>
                                ),
                              )}
                            </select>
                          </label>
                        </details>
                      )}
                      {imageGroups.length === 0 ? (
                        <EmptySection>
                          {imageCategory === "ALL"
                            ? "No image evidence is available for this voyage."
                            : "No images match this category. Choose All images to see the other evidence."}
                          {relatedVoyages
                            .filter(
                              (voyage) => voyage.voyageId !== detail.voyage.voyageId && (voyage.imageCount ?? 0) > 0,
                            )
                            .map((voyage) => (
                              <button
                                key={voyage.voyageId}
                                type="button"
                                className="mx-2 mt-2 block text-[#176B62] underline dark:text-teal-200"
                                onClick={() =>
                                  navigate(`/hold-cleaning/voyages/${encodeURIComponent(voyage.voyageId)}`)
                                }
                              >
                                View {voyage.imageCount} photos on voyage {voyage.voyageNumber}
                              </button>
                            ))}
                        </EmptySection>
                      ) : (
                        <div className="space-y-5">
                          {imageGroups.map(([hold, holdImages]) => (
                            <div key={hold}>
                              <div className="mb-3 flex items-center justify-between gap-3">
                                <h3 className="font-semibold">{hold}</h3>
                                <span className="text-xs text-[#899996] dark:text-gray-300">
                                  {holdImages.length} images
                                </span>
                              </div>
                              <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                                {holdImages.map((image) => (
                                  <article
                                    key={image.imageId}
                                    className="overflow-hidden rounded-lg border border-[#DFE4E2] dark:border-gray-700"
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setPreviewImageIndex(
                                          previewImages.findIndex((item) => item.imageId === image.imageId),
                                        )
                                      }
                                      aria-label={`Preview ${hold} cleaning image`}
                                      className="group relative block w-full overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475]"
                                    >
                                      <HoldCleaningAuthenticatedImage
                                        imageId={image.imageId}
                                        alt={`${hold} cleaning evidence`}
                                      />
                                      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-lg bg-[#17211F]/85 px-2 py-1 text-xs font-semibold text-white group-hover:bg-[#17211F]">
                                        <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
                                        Preview
                                      </span>
                                    </button>
                                    <div className="p-3">
                                      <div className="flex items-center justify-between gap-2">
                                        <HoldCleaningSourceHint source={image.source} />
                                        <span className="text-xs text-[#899996] dark:text-gray-300">
                                          {formatHoldCleaningDate(image.capturedAt, false)}
                                        </span>
                                      </div>
                                      {image.caption && (
                                        <p className="mt-2 text-xs text-[#4A5655] dark:text-gray-300">
                                          {image.caption}
                                        </p>
                                      )}
                                      <p className="mt-3 text-xs text-[#899996] dark:text-gray-300">Latest rating: —</p>
                                    </div>
                                  </article>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    <section className={sectionClass}>
                      <h2 className="text-lg font-semibold">Failed holds and reasons</h2>
                      {detail.history?.failedHolds.length ? (
                        <div className="mt-4 space-y-3">
                          {detail.history.failedHolds.map((failure) => (
                            <article
                              key={`${failure.holdNumber}-${failure.stage}`}
                              className="rounded-lg border border-[#F3D4DC] bg-[#FDF2F5] p-4 dark:border-rose-800 dark:bg-rose-950"
                            >
                              <p className="text-sm font-semibold text-[#A6294F] dark:text-rose-200">
                                Hold {failure.holdNumber} - {failure.stage}
                              </p>
                              <p className="mt-2 text-sm text-[#4A5655] dark:text-gray-300">{failure.reason}</p>
                              {failure.resolution && (
                                <p className="mt-2 text-xs text-[#899996] dark:text-gray-300">
                                  Resolution: {failure.resolution}
                                </p>
                              )}
                              <p className="mt-2 text-xs text-[#899996] dark:text-gray-300">
                                {failure.state ?? "OPEN"}
                                <HoldCleaningSourceHint source={failure.source} />
                              </p>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-4">
                          <EmptySection>No failures reported.</EmptySection>
                        </div>
                      )}
                    </section>

                    <section className={sectionClass}>
                      <div className="flex items-center gap-3">
                        <WrenchScrewdriverIcon
                          className="h-6 w-6 text-[#1F8475] dark:text-teal-300"
                          aria-hidden="true"
                        />
                        <h2 className="text-lg font-semibold">Maintenance and cleaning events</h2>
                      </div>
                      {detail.history?.maintenanceEvents.length ? (
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          {detail.history.maintenanceEvents.map((event) => (
                            <article
                              key={`${event.title}-${event.occurredAt ?? "unknown"}`}
                              className="rounded-lg border border-[#DFE4E2] p-4 dark:border-gray-700"
                            >
                              <p className="text-sm font-semibold">{event.title}</p>
                              <p className="mt-2 text-sm text-[#4A5655] dark:text-gray-300">{event.detail}</p>
                              <p className="mt-2 text-xs text-[#899996] dark:text-gray-300">
                                {formatHoldCleaningDate(event.occurredAt, false)}
                                <HoldCleaningSourceHint source={event.source} />
                              </p>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-4">
                          <EmptySection>No maintenance or cleaning events reported.</EmptySection>
                        </div>
                      )}
                    </section>

                    <div className="grid gap-5 lg:grid-cols-2">
                      <section className={sectionClass}>
                        <div className="flex items-center gap-3">
                          <LinkIcon className="h-6 w-6 text-[#1F8475] dark:text-teal-300" aria-hidden="true" />
                          <h2 className="text-lg font-semibold">Linked documents</h2>
                        </div>
                        {detail.history?.linkedDocuments.length ? (
                          <ul className="mt-4 space-y-2">
                            {detail.history.linkedDocuments.map((document) => (
                              <li
                                key={document.documentId}
                                className="rounded-lg bg-[#F8F9F9] px-3 py-2 text-sm dark:bg-gray-900"
                              >
                                {document.label}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="mt-4">
                            <EmptySection>No linked documents reported.</EmptySection>
                          </div>
                        )}
                      </section>

                      <section className={sectionClass}>
                        <div className="flex items-center justify-between gap-3">
                          <h2 className="text-lg font-semibold">User notes</h2>
                          <button
                            type="button"
                            onClick={() => setNoteOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#C7D0CE] px-3 py-2 text-xs font-semibold text-[#176B62] dark:border-gray-600 dark:text-teal-200"
                          >
                            <PlusIcon className="h-4 w-4" aria-hidden="true" /> Add note
                          </button>
                        </div>
                        {detail.history?.notes.length ? (
                          <ul className="mt-4 space-y-3">
                            {detail.history.notes.map((note) => (
                              <li key={note.noteId} className="rounded-lg bg-[#F8F9F9] p-3 dark:bg-gray-900">
                                <p className="text-sm">{note.text}</p>
                                <p className="mt-2 text-xs text-[#899996] dark:text-gray-300">
                                  {note.author} - {formatHoldCleaningDate(note.createdAt, false)}
                                </p>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="mt-4">
                            <EmptySection>No notes recorded.</EmptySection>
                          </div>
                        )}
                      </section>
                    </div>

                    {assessment && (
                      <div id="hold-cleaning-assessment">
                        <HoldCleaningAssessmentPanel
                          voyageId={detail.voyage.voyageId}
                          initial={assessment}
                          imageIds={images.map((image) => image.imageId)}
                          canUseAi={true}
                          canEdit={true}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tab === "suggestions" && (
                <div className="grid gap-5 xl:grid-cols-2">
                  <section className={twMerge(sectionClass, "xl:col-span-2")}>
                    <h2 className="text-lg font-semibold">Outstanding cleaning items</h2>
                    <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                      Open and resolved items reported for this voyage.
                    </p>
                    {detail.currentCleaning?.outstandingItems?.length ? (
                      <div className="mt-5 grid gap-4 md:grid-cols-2">
                        {detail.currentCleaning.outstandingItems.map((item, index) => (
                          <article
                            key={`${item.description}-${index}`}
                            className="rounded-lg border border-[#DFE4E2] p-4 dark:border-gray-700"
                          >
                            <span
                              className={twMerge(
                                "rounded-full px-2.5 py-1 text-xs font-semibold",
                                item.state === "OPEN"
                                  ? "bg-[#FEF3E2] text-[#B25E09] dark:bg-amber-950 dark:text-amber-200"
                                  : "bg-[#DFF3E7] text-[#2B8A57] dark:bg-emerald-950 dark:text-emerald-200",
                              )}
                            >
                              {item.state}
                            </span>
                            <p className="mt-3 text-sm font-semibold">{item.description}</p>
                            <p className="mt-2 text-xs text-[#899996] dark:text-gray-300">
                              Holds: {item.holdNumbers.join(", ") || "all"}
                              <HoldCleaningSourceHint source={item.source} />
                            </p>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-4">
                        <EmptySection>No outstanding cleaning items reported.</EmptySection>
                      </div>
                    )}
                  </section>
                  <section className={sectionClass}>
                    <div className="mb-4 flex items-center gap-3">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-[#B25E09] dark:text-amber-200"
                        aria-hidden="true"
                      />
                      <div>
                        <h2 className="text-lg font-semibold">Warnings</h2>
                        <p className="text-sm text-[#4A5655] dark:text-gray-300">
                          Source and evidence conditions requiring review.
                        </p>
                      </div>
                    </div>
                    {warningMessages.length === 0 ? (
                      <EmptySection>No source or evidence warnings were reported.</EmptySection>
                    ) : (
                      <ul className="space-y-3">
                        {warningMessages.map((warning, index) => (
                          <li
                            key={`${warning}-${index}`}
                            className="rounded-lg border border-[#F5C77D] bg-[#FFF7E8] p-4 text-sm text-[#713F12] dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
                          >
                            {warning}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  <section className={sectionClass}>
                    <div className="mb-4 flex items-center gap-3">
                      <ClockIcon className="h-6 w-6 text-[#1F8475] dark:text-teal-300" aria-hidden="true" />
                      <div>
                        <h2 className="text-lg font-semibold">Required actions</h2>
                        <p className="text-sm text-[#4A5655] dark:text-gray-300">
                          Actions from the latest readiness assessment.
                        </p>
                      </div>
                    </div>
                    {!assessment || assessment.assessment.payload.actions.length === 0 ? (
                      <EmptySection>No required actions are recorded.</EmptySection>
                    ) : (
                      <ul className="space-y-3">
                        {assessment.assessment.payload.actions.map((action, index) => (
                          <li
                            key={`${action.action}-${index}`}
                            className="rounded-lg border border-[#BBDDD8] bg-[#E9F4F3] p-4 dark:border-teal-800 dark:bg-teal-950"
                          >
                            <p className="text-sm font-semibold text-[#176B62] dark:text-teal-200">{action.action}</p>
                            <p className="mt-2 text-xs text-[#4A5655] dark:text-gray-300">
                              Owner: {action.owner ?? "Not assigned"} - Due:{" "}
                              {formatHoldCleaningDate(action.dueAt, false)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  {assessment && (
                    <div className="xl:col-span-2">
                      <HoldCleaningAssessmentPanel
                        voyageId={detail.voyage.voyageId}
                        initial={assessment}
                        imageIds={images.map((image) => image.imageId)}
                        canUseAi={true}
                        canEdit={true}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </main>
        <HoldCleaningManualEntryModal
          open={manualEntryOpen}
          numberOfHolds={detail?.voyage.numberOfHolds}
          onClose={() => setManualEntryOpen(false)}
          onSave={saveManualEntry}
        />
        <HoldCleaningPhotoUploadModal
          open={photoUploadOpen}
          numberOfHolds={detail?.voyage.numberOfHolds}
          onClose={() => setPhotoUploadOpen(false)}
          onSave={savePhotos}
        />
        <HoldCleaningNoteModal open={noteOpen} onClose={() => setNoteOpen(false)} onSave={saveNote} />
        <HoldCleaningImagePreviewModal
          image={previewImage}
          position={previewImageIndex === null ? 0 : previewImageIndex + 1}
          total={previewImages.length}
          onClose={() => setPreviewImageIndex(null)}
          onPrevious={showPreviousImage}
          onNext={showNextImage}
        />
        <PageFooter />
      </div>
    </div>
  );
};

export default HoldCleaningVoyagePage;
